import {
  StepCommandType,
  StepsType,
} from "container/Project/ProjectData/sideBar/steps";
import { create } from "zustand";
import _ from "lodash";
import { DatasetInfo } from "store/useProjectDataStore";

export type AttachStepType = "join" | "lookup" | "append";

export interface ErrorStepsInfoType {
  orderNum: number;
  errorMsg: string;
  parentOrderNum?: number;
}

export interface DatasetInfoType {
  datasetInfo: DatasetInfo;
  position: {
    left: number;
    top: number;
  };
}

interface StepsStoreType {
  isAttachStepMode: boolean;
  isStepCopyMode: boolean;
  isStepEditMode: boolean;
  currentInitialStep: StepsType | null;
  previousRequestStep: StepsType[] | null;
  previousResponseStep: StepsType[] | null;
  originStepList: StepsType[]; // 스텝 수정모드 진입전, 기존 stepsList를 저장하기 위한 용도
  defaultStepData: StepsType;
  requestStep: number;
  stepsList: StepsType[]; // 프로젝트에 적용된 스텝 리스트
  attachStepsList: StepsType[];
  errorStepsInfo: ErrorStepsInfoType[];
  isStepModified: boolean;
  previewColumnList: string[];
  previewAttachLeftColumnList: string[];
  previewAttachRightColumnList: string[];
  cancelTrigger: number;
  saveTrigger: number;
  currentStepId: string;
  currentStepOrderNum: number;
  currentAttachStepOrderNum: number;
  targetLensId: string | undefined;
  isLensDuplicate: boolean;
  isRefreshMode: boolean;
  refreshResetTrigger: number;
  setAttachStepMode: (value: boolean) => void;
  setIsStepCopyMode: (value: boolean) => void;
  setIsStepEditMode: (value: boolean) => void;
  setCurrentInitialStep: (step?: StepsType) => void;
  setPreviousRequestStep: (stepList: StepsType[]) => void;
  setPreviousResponseStep: (stepList: StepsType[]) => void;
  getStepMaxOrderNum: () => number;
  setOriginStepList: (stepsList: StepsType[]) => void;
  setIsStepModified: (value: boolean) => void;
  setErrorStepsInfo: (errInfo: ErrorStepsInfoType[]) => void;
  setStepsList: (steps: StepsType[], notSendRequest?: boolean) => void;
  setAttachStepsList: (steps: StepsType[], notSendRequest?: boolean) => void;
  addStepsList: (step: StepsType, notSendRequest?: boolean) => void;
  addAttachStepsList: (step: any, notSendRequest?: boolean) => void;
  delStepsList: (orderNum: number) => void;
  delAttachStepsList: (orderNum: number, parentStep: StepsType) => void;
  delUnsavedStepsList: () => void;
  modifiedStep: (
    orderNum: number,
    step: StepsType,
    notSendRequest?: boolean,
    resetCacheKey?: boolean,
    deleteOrderNum?: number
  ) => void;
  modifiedAttachStep: (
    orderNum: number,
    step: StepsType,
    notSendRequest?: boolean,
    resetCacheKey?: boolean,
    deleteOrderNum?: number
  ) => void;
  changeUseYn: (orderNum: number, useYn: 1 | 0) => void;
  changeAttachUseYn: (orderNum: number, useYn: 1 | 0) => void;
  setPreviewColumnList: (list: string[]) => void;
  setPreviewAttachLeftColumnList: (list: string[]) => void;
  setPreviewAttachRightColumnList: (list: string[]) => void;
  setCurrentStepId: (id: string) => void;
  setCurrentStepOrderNum: (orderNum: number) => void;
  setCurrentAttachStepOrderNum: (orderNum: number) => void;
  requestStepTrigger: () => void;
  cancelStepsTrigger: () => void;
  saveStepsTrigger: () => void;
  setTargetLensId: (id: string | undefined) => void;
  setIsLensDuplicate: (isDuplicated: boolean) => void;
  setIsRefreshMode: (isRefreshMode: boolean) => void;
  setRefreshResetTrigger: () => void;
  resetStepsStore: () => void;
}

const useStepsStore = create<StepsStoreType>((set, get) => ({
  isAttachStepMode: false,
  isStepCopyMode: false,
  isStepEditMode: false,
  currentInitialStep: null,
  previousRequestStep: null,
  previousResponseStep: null,
  originStepList: [],
  defaultStepData: {
    useYn: 1,
    stepId: "",
    command: null,
    newLens: [],
    orderNum: 0,
    previewFlag: false,
    stepFilterList: [],
    stepSortList: [],
    stepColumnInfoList: null,
    annotate: null,
    cacheKey: null,
    createdBy: null,
    stepErrMsg: null,
    createdDate: null,
    lastModifiedBy: null,
    lastModifiedDate: null,
    projectVersionId: null,
  },
  requestStep: 0,
  errorStepsInfo: [],
  stepsList: [],
  attachStepsList: [],
  isStepModified: false,
  previewColumnList: [],
  previewAttachLeftColumnList: [],
  previewAttachRightColumnList: [],
  cancelTrigger: 0,
  saveTrigger: 0,
  currentStepId: "",
  currentStepOrderNum: 0,
  currentAttachStepOrderNum: 0,
  targetLensId: "",
  isLensDuplicate: false,
  isRefreshMode: false,
  refreshResetTrigger: 0,
  setAttachStepMode: (value: boolean) => {
    set({ isAttachStepMode: value });
  },
  setIsStepCopyMode: (view: boolean) => {
    set({ isStepCopyMode: view });
  },
  setIsStepEditMode: (view: boolean) => {
    set({ isStepEditMode: view });
  },
  setCurrentInitialStep: (step?: StepsType) => {
    set({ currentInitialStep: step || null });
  },
  setPreviousRequestStep: (stepList: StepsType[]) => {
    set({ previousRequestStep: stepList });
  },
  setPreviousResponseStep: (stepList: StepsType[]) => {
    set({ previousResponseStep: stepList });
  },
  getStepMaxOrderNum: (): number => {
    let maxOrderNum = 0;
    set((state) => {
      const allStepsList = _.cloneDeep(state.stepsList);
      maxOrderNum = allStepsList.reduce((max: any, step: any) => {
        return step.orderNum > max ? step.orderNum : max;
      }, 0);
      return { ...state };
    });
    return maxOrderNum;
  },
  setOriginStepList: (stepsList: StepsType[]) => {
    set({ originStepList: stepsList });
  },
  setIsStepModified: (value: boolean) => {
    set({ isStepModified: value });
  },
  setErrorStepsInfo: (errInfo: ErrorStepsInfoType[]) => {
    set({ errorStepsInfo: errInfo });
  },
  setStepsList: (steps: StepsType[], notSendRequest?: boolean) => {
    set((state) => ({
      stepsList: steps,
      requestStep: notSendRequest ? state.requestStep : state.requestStep + 1,
    }));
  },
  setAttachStepsList: (steps: StepsType[], notSendRequest?: boolean) => {
    set((state) => {
      const findTargetStep = _.cloneDeep(state.stepsList).find(
        (step) => step.orderNum === state.currentStepOrderNum
      );
      const attachMode: AttachStepType =
        (findTargetStep?.join && "join") ||
        (findTargetStep?.append && "append") ||
        (findTargetStep?.lookup && "lookup");

      const updatedStepsList = _.cloneDeep(state.stepsList).map((step) => {
        if (step.orderNum === state.currentStepOrderNum && step[attachMode]) {
          step[attachMode].attachStepList = steps;
          return step;
        } else return step;
      });
      return {
        stepsList: updatedStepsList,
        requestStep: notSendRequest ? state.requestStep : state.requestStep + 1,
      };
    });
  },
  addStepsList: (step: StepsType) => {
    set((state) => {
      let updatedStepsList = _.cloneDeep(state.stepsList).sort(
        (a, b) => a.orderNum - b.orderNum
      );
      const stepData = Object.assign({ ...state.defaultStepData }, step);
      const indexToInsert = updatedStepsList.findIndex(
        (item) => item.orderNum === stepData.orderNum
      );
      if (indexToInsert !== -1) {
        updatedStepsList.splice(indexToInsert, 0, stepData);
        for (let i = 0; i < updatedStepsList.length; i++) {
          updatedStepsList[i].orderNum = i;
        }
      } else {
        updatedStepsList.push(stepData);
      }
      updatedStepsList = updatedStepsList.map((item) => {
        if (item.orderNum > step.orderNum) {
          return { ...item, cacheKey: null, stepErrMsg: null };
        } else {
          return { ...item };
        }
      });
      return {
        ...state,
        stepsList: updatedStepsList,
        isStepEditMode: true,
      };
    });
  },
  addAttachStepsList: (step: any, notSendRequest?: boolean) => {
    set((state) => {
      let findTargetStep = _.cloneDeep(state.stepsList).find(
        (step) => step.orderNum === state.currentStepOrderNum
      );
      if (findTargetStep) {
        let attachMode: AttachStepType =
          (findTargetStep?.join && "join") ||
          (findTargetStep?.append && "append") ||
          (findTargetStep?.lookup && "lookup");

        let updatedAttachStepsList = [
          ...findTargetStep[attachMode].attachStepList,
        ].sort((a, b) => a.orderNum - b.orderNum);

        const stepData = Object.assign({ ...state.defaultStepData }, step);
        const indexToInsert = updatedAttachStepsList.findIndex(
          (item) => item.orderNum === stepData.orderNum
        );

        if (indexToInsert !== -1) {
          updatedAttachStepsList.splice(indexToInsert, 0, stepData);
          for (let i = 0; i < updatedAttachStepsList.length; i++) {
            updatedAttachStepsList[i].orderNum = i;
          }
        } else {
          updatedAttachStepsList.push(stepData);
        }

        updatedAttachStepsList = updatedAttachStepsList.map((item) => {
          if (item.orderNum > step.orderNum) {
            return { ...item, cacheKey: null, stepErrMsg: null };
          } else {
            return { ...item };
          }
        });

        const updatedStepsList = _.cloneDeep(state.stepsList).map((step) => {
          if (step.orderNum === state.currentStepOrderNum) {
            step[attachMode].attachStepList = updatedAttachStepsList;
            return step;
          } else return { ...step, previewFlag: false };
        });

        return {
          ...state,
          stepsList: updatedStepsList,
          isStepEditMode: true,
        };
      } else return { ...state };
    });
  },
  delStepsList: (orderNum: number) => {
    // 스텝 삭제 시 다음에 선택될 스텁으로 삭제된 스텝의 렌즈를 옮겨주는 코드
    set((state) => {
      let tempLens: any;
      const tempList = _.cloneDeep(state.stepsList);
      let filterStepsList = tempList.filter((item: StepsType) => {
        // 삭제될 스텝의 렌즈를 저장
        if (item.orderNum === orderNum && item.newLens) {
          tempLens = item.newLens;
        }
        return item.orderNum !== orderNum;
      });

      const targetIdx = filterStepsList.findIndex(
        (step: StepsType) => step.orderNum === state.currentStepOrderNum
      );
      if (targetIdx !== -1) {
        let prevLensList: any = [];
        if (filterStepsList[targetIdx]) {
          if (filterStepsList[targetIdx].newLens) {
            prevLensList = filterStepsList[targetIdx].newLens;
          }
          if (prevLensList && tempLens) {
            filterStepsList[targetIdx].newLens = [...prevLensList, ...tempLens];
          }
        }
      }

      filterStepsList = filterStepsList.map((item) => {
        if (item.orderNum > orderNum) {
          return { ...item, cacheKey: null, stepErrMsg: null };
        } else {
          return item;
        }
      });
      return {
        ...state,
        requestStep: state.requestStep + 1,
        stepsList: filterStepsList,
      };
    });
  },
  delAttachStepsList: (orderNum: number, parentStep: StepsType) => {
    // 스텝 삭제 시 다음에 선택될 스텁으로 삭제된 스텝의 렌즈를 옮겨주는 코드
    set((state) => {
      let tempLens: any;
      let attachMode: AttachStepType =
        (parentStep?.join && "join") ||
        (parentStep?.append && "append") ||
        (parentStep?.lookup && "lookup");

      const tempList = _.cloneDeep(parentStep[attachMode].attachStepList);
      let filterStepsList = tempList.filter((item: StepsType) => {
        // 삭제될 스텝의 렌즈를 저장
        if (item.orderNum === orderNum && item.newLens) {
          tempLens = item.newLens;
        }
        return item.orderNum !== orderNum;
      });

      const targetIdx = filterStepsList.findIndex(
        (step: StepsType) => step.orderNum === state.currentAttachStepOrderNum
      );
      if (targetIdx !== -1) {
        let prevLensList: any = [];
        if (filterStepsList[targetIdx]) {
          if (filterStepsList[targetIdx].newLens) {
            prevLensList = filterStepsList[targetIdx].newLens;
          }
          filterStepsList[targetIdx].newLens = [...prevLensList, ...tempLens];
        }
      }

      filterStepsList = filterStepsList.map((item: StepsType) => {
        if (item.orderNum > orderNum) {
          return { ...item, cacheKey: null, stepErrMsg: null };
        } else {
          return item;
        }
      });

      const updatedStepsList = _.cloneDeep(state.stepsList).map((step) => {
        if (step.orderNum === state.currentStepOrderNum) {
          step[attachMode].attachStepList = filterStepsList;
          return step;
        } else return step;
      });
      return {
        ...state,
        requestStep: state.requestStep + 1,
        stepsList: updatedStepsList,
      };
    });
  },
  delUnsavedStepsList: () => {
    set((state) => ({
      ...state,
      requestStep: state.requestStep + 1,
      stepsList: state.stepsList,
      unsavedStepsList: [],
    }));
  },
  /**
   * 특정 orderNum의 스텝 데이터를 변경하는 함수
   * @param orderNum 수정할 스텝의 orderNum
   * @param step 수정된 스텝 데이터 (해당 스텝으로 덮어씌움)
   */
  modifiedStep: (
    orderNum: number,
    step: StepsType,
    notSendRequest?: boolean,
    resetCacheKey?: boolean,
    deleteOrderNum?: number
  ) => {
    set((state) => {
      const filterStepsList = _.cloneDeep(state.stepsList)
        .filter((step) => step.orderNum !== deleteOrderNum)
        .map((item) => {
          if (item.orderNum === orderNum) {
            return {
              ...step,
              cacheKey: resetCacheKey ? null : item.cacheKey,
              stepErrMsg: item.stepErrMsg,
              stepColumnInfoList: item.stepColumnInfoList,
            };
          } else {
            return {
              ...item,
              previewFlag: false,
              cacheKey:
                resetCacheKey && item.orderNum > orderNum
                  ? null
                  : item.cacheKey,
              stepErrMsg: item.stepErrMsg,
              stepColumnInfoList: item.stepColumnInfoList,
            };
          }
        });
      return {
        ...state,
        requestStep: notSendRequest ? state.requestStep : state.requestStep + 1,
        stepsList: filterStepsList,
      };
    });
  },
  modifiedAttachStep: (
    orderNum: number,
    step: StepsType,
    notSendRequest?: boolean,
    resetCacheKey?: boolean,
    deleteOrderNum?: number
  ) => {
    set((state) => {
      const findTargetStep = _.cloneDeep(state.stepsList).find(
        (step) => step.orderNum === state.currentStepOrderNum
      );
      if (findTargetStep) {
        let attachMode: AttachStepType =
          (findTargetStep?.join && "join") ||
          (findTargetStep?.append && "append") ||
          (findTargetStep?.lookup && "lookup");

        const filterStepsList = findTargetStep?.[attachMode]?.attachStepList
          .filter((step: StepsType) => step.orderNum !== deleteOrderNum)
          .map((item: StepsType) => {
            if (item.orderNum === orderNum) {
              return {
                ...step,
                cacheKey: resetCacheKey ? null : step.cacheKey,
                stepErrMsg: step.stepErrMsg,
                stepColumnInfoList: step.stepColumnInfoList,
              };
            } else {
              return {
                ...item,
                previewFlag: false,
                stepColumnInfoList: item.stepColumnInfoList,
                cacheKey:
                  resetCacheKey && item.orderNum > orderNum
                    ? null
                    : item.cacheKey,
                stepErrMsg: item.stepErrMsg,
              };
            }
          });

        const updatedStepsList = _.cloneDeep(state.stepsList).map((step) => {
          if (step.orderNum === state.currentStepOrderNum) {
            step[attachMode].attachStepList = filterStepsList;
            return step;
          } else {
            return { ...step, previewFlag: false };
          }
        });
        return {
          ...state,
          requestStep: notSendRequest
            ? state.requestStep
            : state.requestStep + 1,
          stepsList: updatedStepsList,
        };
      } else return { ...state };
    });
  },
  changeUseYn: (orderNum: number, useYn: 1 | 0) => {
    set((state) => {
      const { originStepList, stepsList } = get();
      const updatedStepsList = _.cloneDeep(stepsList).map((step: StepsType) => {
        if (step.orderNum === orderNum) {
          step = { ...step, useYn: useYn, cacheKey: null };
        }
        if (step.orderNum > orderNum) {
          step = { ...step, cacheKey: null };
        }
        return step;
      });
      const updatedOriginStepsList = _.cloneDeep(originStepList).map(
        (step: StepsType) => {
          if (step.orderNum === orderNum) {
            step = { ...step, useYn: useYn, cacheKey: null };
          }
          if (step.orderNum > orderNum) {
            step = { ...step, cacheKey: null };
          }
          return step;
        }
      );
      return {
        ...state,
        requestStep: state.requestStep + 1,
        stepsList: updatedStepsList,
        originStepList: updatedOriginStepsList,
      };
    });
  },
  changeAttachUseYn: (orderNum: number, useYn: 1 | 0) => {
    set((state) => {
      let findTargetStep = _.cloneDeep(state.stepsList).find(
        (step) => step.orderNum === state.currentStepOrderNum
      );
      if (findTargetStep) {
        let attachMode: AttachStepType =
          (findTargetStep?.join && "join") ||
          (findTargetStep?.append && "append") ||
          (findTargetStep?.lookup && "lookup");

        let updatedAttachStepsList = [
          ...findTargetStep[attachMode].attachStepList,
        ]
          .map((step) => {
            if (step.orderNum === orderNum) {
              step = { ...step, useYn: useYn, cacheKey: null };
            }
            if (step.orderNum > orderNum) {
              step = { ...step, cacheKey: null };
            }
            return step;
          })
          .sort((a, b) => a.orderNum - b.orderNum);

        const updatedStepsList = _.cloneDeep(state.stepsList).map((step) => {
          if (step.orderNum === state.currentStepOrderNum) {
            step[attachMode].attachStepList = updatedAttachStepsList;
            return step;
          } else return { ...step, previewFlag: false };
        });
        return {
          ...state,
          requestStep: state.requestStep + 1,
          stepsList: updatedStepsList,
        };
      } else return { ...state };
    });
  },
  setPreviewColumnList: (list: string[]) => {
    set({ previewColumnList: list });
  },
  setPreviewAttachLeftColumnList: (list: string[]) => {
    set({ previewAttachLeftColumnList: list });
  },
  setPreviewAttachRightColumnList: (list: string[]) => {
    set({ previewAttachRightColumnList: list });
  },
  setCurrentStepId: (id: string) => {
    set({ currentStepId: id });
  },
  setCurrentStepOrderNum: (orderNum: number) => {
    set({ currentStepOrderNum: orderNum });
  },
  setCurrentAttachStepOrderNum: (orderNum: number) => {
    set({ currentAttachStepOrderNum: orderNum });
  },
  requestStepTrigger: () => {
    set((state) => ({ requestStep: state.requestStep + 1 }));
  },
  cancelStepsTrigger: () => {
    set((state) => ({ cancelTrigger: state.cancelTrigger + 1 }));
  },
  saveStepsTrigger: () => {
    set((state) => ({ saveTrigger: state.saveTrigger + 1 }));
  },
  setTargetLensId: (id: string | undefined) => {
    set({ targetLensId: id });
  },
  setIsLensDuplicate: (isDuplicate: boolean) => {
    set({ isLensDuplicate: isDuplicate });
  },
  setIsRefreshMode: (isRefreshMode: boolean) =>
    set((state) => ({ isRefreshMode: isRefreshMode })),
  setRefreshResetTrigger: () =>
    set((state) => ({ refreshResetTrigger: state.refreshResetTrigger })),
  resetStepsStore: () => {
    // 데이터셋 변경 시에 view를 두 번 호출하는 문제를 해결하기 위해 parameter 추가
    // requestStep 변경 시 문제가 되므로 해당 isChangeDataset일 때는 해당 값만 초기화 안 해줌
    set((state) => {
      return {
        isAttachStepMode: false,
        isStepCopyMode: false,
        isStepEditMode: false,
        currentInitialStep: null,
        previousResponseStep: null,
        originStepList: [],
        defaultStepData: {
          useYn: 1,
          stepId: "",
          command: null,
          newLens: [],
          orderNum: 0,
          previewFlag: false,
          stepSortList: [],
          stepFilterList: [],
          stepColumnInfoList: [],
          annotate: null,
          cacheKey: null,
          createdBy: null,
          stepErrMsg: null,
          createdDate: null,
          lastModifiedBy: null,
          lastModifiedDate: null,
          projectVersionId: null,
        },
        // requestStep: isChangeDataset ? state.requestStep : 0,
        errorStepsInfo: [],
        // stepsList: [],
        // attachStepsList: [],
        isStepModified: false,
        previewColumnList: [],
        previewAttachLeftColumnList: [],
        previewAttachRightColumnList: [],
        cancelTrigger: 0,
        saveTrigger: 0,
        // currentStepOrderNum: 0,
        // currentAttachStepOrderNum: 0,
        isRefreshMode: false,
        targetLensId: "",
        isLensDuplicate: false,
      };
    });
  },
}));

export default useStepsStore;
